import { useId, type CSSProperties } from "react";
import { useBrandLogos } from "@/lib/brandLogos";

/**
 * A connected system's own logo, from open-licensed sets (see scripts/brand-icons.mjs).
 * Until the logos have loaded, and for the few systems without an open-licensed logo, it
 * draws nothing and callers show their glyph.
 */

/** Gradient and clip ids must be unique per drawing, or two copies of a logo share defs. */
function withUniqueIds(body: string, suffix: string): string {
  return body
    .replace(/\bid="([^"]+)"/g, (_, id: string) => `id="${id}-${suffix}"`)
    .replace(/url\(#([^)]+)\)/g, (_, id: string) => `url(#${id}-${suffix})`)
    .replace(/href="#([^"]+)"/g, (_, id: string) => `href="#${id}-${suffix}"`);
}

export function BrandLogo({
  brand,
  size = 20,
  title,
  className,
  style,
}: {
  /** A connector key, such as "slack". */
  brand: string;
  size?: number;
  /** Spoken name; without it the logo is decorative. */
  title?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const logos = useBrandLogos();
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const icon = logos?.brandIcon(brand);
  if (!icon) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${icon.w} ${icon.h}`}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={className}
      // Dark marks are drawn in currentColor so they read on navy and on paper alike.
      style={{ display: "block", flex: "0 0 auto", color: "var(--ink)", ...style }}
      dangerouslySetInnerHTML={{ __html: withUniqueIds(icon.body, uid) }}
    />
  );
}
